/**
 * useCheckoutSession Hook
 *
 * React hook for managing subscription changes - both new subscriptions and plan changes.
 *
 * For new subscribers (trial users): Creates a Stripe Checkout session
 * For existing subscribers: Uses plan change with proration (credits unused time)
 *
 * @example
 * Basic usage:
 * ```tsx
 * const { createCheckout, changePlan, isPending } = useCheckoutSession(workspaceId, {
 *   onSuccess: () => console.log('Success!'),
 *   onError: (error) => console.error(error),
 * });
 *
 * // For trial users (new subscribers)
 * createCheckout({ planId, billingInterval: 'monthly' });
 *
 * // For existing subscribers (plan change with proration)
 * changePlan({ planId, billingInterval: 'monthly' });
 * ```
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BillingInterval } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { useRpc } from '@/lib/rpc';
import { useToastStore } from '@/store/useToasts';
import { createApiError, isApiError } from '@/lib/apiErrors';

interface CreateCheckoutParams {
  /**
   * The plan ID to subscribe to
   */
  planId: string;
  /**
   * Billing interval (monthly or yearly)
   */
  billingInterval: BillingInterval;
}

interface UseCheckoutSessionOptions {
  /**
   * Callback invoked when checkout session is successfully created
   * Before redirecting to Stripe
   */
  onSuccess?: (data: { checkout_url?: string; success?: boolean }) => void;
  /**
   * Callback invoked when operation fails
   */
  onError?: (error: Error) => void;
}

interface UseCheckoutSessionResult {
  /**
   * Create checkout session and redirect to Stripe (for new subscribers)
   */
  createCheckout: (params: CreateCheckoutParams) => void;
  /**
   * Change plan with proration (for existing subscribers)
   */
  changePlan: (params: CreateCheckoutParams) => void;
  /**
   * Unified upgrade method - automatically chooses checkout or plan change
   */
  upgrade: (params: CreateCheckoutParams & { hasStripeSubscription: boolean }) => void;
  /**
   * Loading state
   */
  isPending: boolean;
  /**
   * Error state
   */
  error: Error | null;
}

/**
 * Hook for creating Stripe Checkout sessions and changing plans
 *
 * Handles two flows:
 * 1. New subscription: Creates Stripe Checkout session and redirects
 * 2. Plan change: Updates existing subscription with proration
 *
 * @param workspaceId - The workspace ID to create subscription for
 * @param options - Optional callbacks for success/error handling
 * @returns Mutation functions, loading state, and error state
 */
export function useCheckoutSession(
  workspaceId: string,
  options: UseCheckoutSessionOptions = {}
): UseCheckoutSessionResult {
  const { onSuccess, onError } = options;
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const { error: showErrorToast, success: showSuccessToast } = useToastStore();

  // Checkout mutation (for new subscribers / trial users)
  const checkoutMutation = useMutation({
    mutationFn: async ({ planId, billingInterval }: CreateCheckoutParams) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription.checkout.$post({
        param: { id: workspaceId },
        json: {
          plan_id: planId,
          billing_interval: billingInterval,
        }
      });

      if (!res.ok) {
        throw await createApiError(res, 'checkout session');
      }

      return res.json() as Promise<{ checkout_url: string }>;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.subscription(workspaceId) });
      onSuccess?.(data);
      window.location.href = data.checkout_url;
    },
    onError: (error: Error) => {
      const message = isApiError(error) ? error.message : 'Failed to start checkout';
      showErrorToast('Checkout Failed', message);
      onError?.(error);
    },
  });

  // Change plan mutation (for existing subscribers - with proration)
  const changePlanMutation = useMutation({
    mutationFn: async ({ planId, billingInterval }: CreateCheckoutParams) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.workspaces[':id'].subscription['change-plan'].$post({
        param: { id: workspaceId },
        json: {
          plan_id: planId,
          billing_interval: billingInterval,
        }
      });

      if (!res.ok) {
        throw await createApiError(res, 'plan change');
      }

      return res.json() as Promise<{ success: boolean; plan: { id: string; name: string } }>;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workspace.subscription(workspaceId) });
      showSuccessToast(
        'Plan Changed',
        `Successfully switched to ${data.plan.name}. Proration has been applied immediately.`
      );
      onSuccess?.({ success: true });
    },
    onError: (error: Error) => {
      const message = isApiError(error) ? error.message : 'Failed to change plan';
      showErrorToast('Plan Change Failed', message);
      onError?.(error);
    },
  });

  const upgrade = useCallback(
    (params: CreateCheckoutParams & { hasStripeSubscription: boolean }) => {
      if (params.hasStripeSubscription) {
        changePlanMutation.mutate({ planId: params.planId, billingInterval: params.billingInterval });
      } else {
        checkoutMutation.mutate({ planId: params.planId, billingInterval: params.billingInterval });
      }
    },
    [changePlanMutation, checkoutMutation]
  );

  return {
    createCheckout: checkoutMutation.mutate,
    changePlan: changePlanMutation.mutate,
    upgrade,
    isPending: checkoutMutation.isPending || changePlanMutation.isPending,
    error: (checkoutMutation.error || changePlanMutation.error) as Error | null,
  };
}
